import { useCallback, useState } from "react";
import useNotification from "../../../../hooks/useNotification";

export const useVolume = ({
  setVolumes,
  setProducts,
  data,
  volumes,
  assetsData,
}) => {
  const { showNotification } = useNotification();

  // * Volumes
  const [newVolume, setNewVolume] = useState({ name: "" });
  const [openVolumesModal, setOpenVolumesModal] = useState(false);
  const [openVolumeSelectModal, setOpenVolumeSelectModal] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState("");
  const [quantity, setQuantity] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleOpenVolumesModal = () => setOpenVolumesModal(true);
  const handleCloseVolumesModal = () => setOpenVolumesModal(false);
  const handleOpenSelectVolumesModal = (item) => {
    setSelectedProduct((prev) => ({
      ...prev,
      ...item,
      product_item: data
        .find((i) => i.identify === item.identify)
        .product_items.find((i) => i.id === item.id),
    }));
    setOpenVolumeSelectModal(true);
  };
  const handleCloseVolumeSelectModal = () => {
    setOpenVolumeSelectModal(false);
    setSelectedVolume("");
    setQuantity("");
    setSelectedProduct(null);
  };

  const getTotalAlreadyAllocated = (
    selectedItem,
    selectedProduct,
    movements,
    volumes,
  ) => {
    if (!selectedItem || !selectedProduct) return 0;

    const productId = selectedProduct?.product_id;

    // Pega todos os product_item_id que pertencem ao mesmo product_id
    const allProductItemIds =
      selectedItem?.product_items
        ?.filter((item) => Number(item.product_id) === Number(productId))
        ?.map((item) => Number(item.id)) || [];

    // Movimentos da mesma OS (exceto o atual)
    const movementsForOrder =
      movements?.filter(
        (m) =>
          m.service_order_id === selectedItem?.service_order_id &&
          m.id !== selectedItem?.movement_id,
      ) ?? [];

    let totalAlreadyAllocated = movementsForOrder.reduce((acc, movement) => {
      const qtyInMovement =
        movement.volumes?.reduce((volAcc, v) => {
          const qtyInVolume =
            v.items
              ?.filter((i) =>
                allProductItemIds.includes(Number(i.product_item_id)),
              )
              ?.reduce((sum, i) => sum + Number(i.quantity), 0) || 0;

          return volAcc + qtyInVolume;
        }, 0) || 0;

      return acc + qtyInMovement;
    }, 0);

    // Soma o que já está nos volumes atuais
    totalAlreadyAllocated +=
      volumes?.reduce((acc, v) => {
        const qtyInThisVolume =
          v.items
            ?.filter((i) =>
              allProductItemIds.includes(Number(i.product_item_id)),
            )
            ?.reduce((sum, i) => sum + Number(i.quantity), 0) || 0;

        return acc + qtyInThisVolume;
      }, 0) || 0;

    return totalAlreadyAllocated;
  };

  let remainingQuantity = null;
  if (selectedProduct) {
    const selectedItem = data.find(
      (d) => d.product_id === selectedProduct?.service_order_item?.product?.id,
    );

    const totalAllowed = selectedItem?.requested_quantity ?? 0;

    const totalAlreadyAllocated = getTotalAlreadyAllocated(
      selectedItem,
      selectedProduct,
      data,
      volumes,
    );

    remainingQuantity = totalAllowed - totalAlreadyAllocated;
  }

  const handleAddItemToVolume = () => {
    if (selectedVolume && quantity && selectedProduct) {
      const selectedItem = data.find(
        (d) =>
          d.product_id === selectedProduct?.service_order_item?.product?.id,
      );
      const totalAllowed = selectedItem?.requested_quantity ?? 0;
      const currentRequested = Number(quantity);

      // PARA O CASO DA QUANTIDADE REQUISITADA
      const totalAlreadyAllocated = getTotalAlreadyAllocated(
        selectedItem,
        selectedProduct,
        data,
        volumes,
        assetsData,
      );

      if (totalAlreadyAllocated + currentRequested > totalAllowed) {
        if (totalAllowed === 0) {
          showNotification(
            "warning",
            `O insumo "${selectedProduct?.alias}" já foi atendido.`,
          );
          return;
        }

        if (totalAllowed - totalAlreadyAllocated === 0) {
          showNotification(
            "warning",
            `Quantidade limite atingida. As ${totalAllowed} unidades requisitadas do insumo "${selectedProduct?.alias}" já foram atendidas.`,
          );
          return;
        }

        showNotification(
          "warning",
          `Quantidade limite excedida. Foram requisitadas ${totalAllowed} unidades do insumo "${
            selectedProduct?.alias
          }", já foram alocadas ${totalAlreadyAllocated}, restam apenas ${
            totalAllowed - totalAlreadyAllocated
          }.`,
        );
        return;
      }

      // PARA O CASO DO ESTOQUE DO PRODUTO
      const currentTotalAlreadyAllocated =
        volumes?.reduce((acc, v) => {
          const qtyInThisVolume =
            v.items
              ?.filter((i) => i.product_item_id === selectedProduct.id)
              ?.reduce((sum, i) => sum + Number(i.quantity), 0) || 0;

          return acc + qtyInThisVolume;
        }, 0) || 0;

      if (
        selectedProduct.product_item?.quantity <
        currentRequested + currentTotalAlreadyAllocated
      ) {
        showNotification(
          "warning",
          `Só restam (${selectedProduct.product_item?.quantity}) unidades disponíveis em estoque`,
        );
        return;
      }
      setVolumes((prev) =>
        prev.map((v) => {
          if (v.name !== selectedVolume) return v;

          const existingItemIndex = v.items.findIndex(
            (i) => i.product_item_id === selectedProduct?.id,
          );

          if (existingItemIndex !== -1) {
            const updatedItems = [...v.items];
            updatedItems[existingItemIndex] = {
              ...updatedItems[existingItemIndex],
              quantity:
                Number(updatedItems[existingItemIndex].quantity) +
                Number(currentRequested),
            };

            return { ...v, items: updatedItems };
          }

          return {
            ...v,
            items: [
              ...v.items,
              {
                product_id: selectedProduct?.service_order_item?.product?.id,
                product_item_id: selectedProduct?.id,
                product_alias: selectedProduct?.alias,
                code: selectedProduct?.code,
                category: selectedProduct?.product_item?.category?.name,
                condition: selectedProduct?.product_item?.condition?.name,
                address: `${selectedProduct?.product_item?.address?.addressable?.alias} | ${selectedProduct?.product_item?.address?.formatted_address}`,
                location:
                  selectedProduct?.product_item?.location?.formatted_location,
                quantity: Number(currentRequested),
              },
            ],
          };
        }),
      );

      // Atualizar data DEPOIS de atualizar volumes
      setProducts((prevData) =>
        prevData.map((d) => {
          if (d.product_id !== selectedProduct?.service_order_item?.product?.id)
            return d;
          const newQuantity = d.quantity - currentRequested;
          return { ...d, quantity: newQuantity < 0 ? 0 : newQuantity };
        }),
      );

      if (remainingQuantity - currentRequested === 0) {
        handleCloseVolumeSelectModal();
      }

      setSelectedVolume("");
      setQuantity("");
    }
  };

  const handleRemoveVolume = useCallback(
    async (volume) => {
      setProducts((prevProducts) =>
        prevProducts.map((product) => {
          const affected = volume?.items?.find(
            (vItem) => vItem.product_id === product.product_id,
          );
          if (affected) {
            return {
              ...product,
              quantity: product.quantity + affected.quantity,
            };
          }
          return product;
        }),
      );

      setVolumes((prev) =>
        prev.filter((v) => v.identifier !== volume.identifier),
      );
    },
    [setProducts, setVolumes],
  );

  const handleEditVolume = useCallback(
    (identifier, newName) => {
      const trimmed = newName.trim();

      // Validação 1: Nome vazio
      if (!trimmed) {
        showNotification(
          "warning",
          "Por favor, digite um nome válido para o volume",
        );
        return false;
      }

      // Validação 2: Nome duplicado (ignorando o próprio volume que está sendo editado)
      const nameExists = volumes?.some(
        (v) =>
          v.identifier !== identifier &&
          v.name.toLowerCase() === trimmed.toLowerCase(),
      );

      if (nameExists) {
        showNotification("warning", "Já existe outro volume com este nome");
        return false;
      }

      // Atualização imutável do Estado
      setVolumes((prev) =>
        prev.map((v) =>
          v.identifier === identifier ? { ...v, name: trimmed } : v,
        ),
      );

      showNotification("success", "Nome do volume atualizado!");
      return true;
    },
    [volumes, setVolumes, showNotification],
  );

  return {
    newVolume,
    setNewVolume,
    openVolumesModal,
    setOpenVolumesModal,
    openVolumeSelectModal,
    setOpenVolumeSelectModal,
    selectedVolume,
    setSelectedVolume,
    quantity,
    setQuantity,
    selectedProduct,
    setSelectedProduct,
    handleOpenVolumesModal,
    handleCloseVolumesModal,
    handleOpenSelectVolumesModal,
    handleCloseVolumeSelectModal,
    handleAddItemToVolume,
    handleRemoveVolume,
    handleEditVolume,
    getTotalAlreadyAllocated,
    remainingQuantity,
  };
};
